import { createContext, useContext, useState, type ReactNode } from 'react'
import { authService } from '../api/services'
import { ApiError } from '../api/client'

interface AuthUser {
  userId: string
  username: string
  fullName: string
  roles: string[]
  tenantId: string
  tenantCode: string
  tenantName: string
  isPlatform: boolean
}

interface AuthContextType {
  user: AuthUser | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (tenantCode: string, username: string, password: string) => Promise<{ success: boolean; errorCode?: string | null; message?: string | null }>
  switchTenant: (tenantId: string) => Promise<{ success: boolean; errorCode?: string | null; message?: string | null }>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | null>(null)

function getStoredUser(): AuthUser | null {
  const stored = localStorage.getItem('auth_user')
  if (stored) {
    try {
      const parsed = JSON.parse(stored)
      // Migrate legacy entries that predate the tenant fields
      if (parsed && typeof parsed === 'object' && 'userId' in parsed) {
        return {
          userId: parsed.userId,
          username: parsed.username,
          fullName: parsed.fullName,
          roles: parsed.roles ?? [],
          tenantId: parsed.tenantId ?? '',
          tenantCode: parsed.tenantCode ?? '',
          tenantName: parsed.tenantName ?? '',
          isPlatform: parsed.isPlatform ?? false,
        }
      }
    } catch {
      localStorage.removeItem('auth_user')
    }
  }
  return null
}

function persistAuth(data: {
  accessToken: string
  refreshToken: string
  userId: string
  username: string
  fullName: string
  roles: string[]
  tenantId: string
  tenantCode: string
  tenantName: string
  isPlatform: boolean
}): AuthUser {
  localStorage.setItem('auth_token', data.accessToken)
  localStorage.setItem('refresh_token', data.refreshToken)
  localStorage.setItem('tenant_id', data.tenantId)

  const authUser: AuthUser = {
    userId: data.userId,
    username: data.username,
    fullName: data.fullName,
    roles: data.roles,
    tenantId: data.tenantId,
    tenantCode: data.tenantCode,
    tenantName: data.tenantName,
    isPlatform: data.isPlatform,
  }
  localStorage.setItem('auth_user', JSON.stringify(authUser))
  return authUser
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(getStoredUser)
  const [isLoading] = useState(false)

  const login = async (tenantCode: string, username: string, password: string) => {
    try {
      const data = await authService.login(tenantCode, username, password)
      const authUser = persistAuth({ ...data, username })
      setUser(authUser)
      return { success: true }
    } catch (error) {
      if (error instanceof ApiError) {
        return { success: false, errorCode: error.errorCode, message: error.message }
      }
      return { success: false, errorCode: null, message: 'Error de conexion' }
    }
  }

  const switchTenant = async (tenantId: string) => {
    try {
      const data = await authService.switchTenant(tenantId)
      const authUser = persistAuth(data)
      setUser(authUser)
      return { success: true }
    } catch (error) {
      if (error instanceof ApiError) {
        return { success: false, errorCode: error.errorCode, message: error.message }
      }
      return { success: false, errorCode: null, message: 'Error de conexion' }
    }
  }

  const logout = async () => {
    const refreshToken = localStorage.getItem('refresh_token')
    if (refreshToken) {
      try {
        await authService.logout(refreshToken)
      } catch {
        // best-effort
      }
    }

    localStorage.removeItem('auth_token')
    localStorage.removeItem('refresh_token')
    localStorage.removeItem('auth_user')
    localStorage.removeItem('tenant_id')
    setUser(null)
    window.location.href = '/login'
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        switchTenant,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
